import { MainLayout } from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Printer, Wifi, Download, Cable, Monitor, BookOpen } from "lucide-react";
import { saveMethod } from "@/utils/printService";

import { usePrinterSetup } from "@/hooks/usePrinterSetup";
import { useSerialPrinter } from "@/hooks/useSerialPrinter";
import { SerialPrinterSection } from "@/components/printer/SerialPrinterSection";
import { DesktopMethodSection } from "@/components/printer/DesktopMethodSection";
import { BrowserMethodSection } from "@/components/printer/BrowserMethodSection";
import { QZTraySection } from "@/components/printer/QZTraySection";
import { PrintDiagnosticCard } from "@/components/printer/PrintDiagnosticCard";

export default function PrinterSetupPage() {
  const setup = usePrinterSetup();
  const serial = useSerialPrinter();
  const { method, setMethod } = setup;

  return (
    <MainLayout>
      <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="printer-setup-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Printer className="w-6 h-6" /> Configuracao de Impressora
          </h1>
          <p className="text-sm text-gray-500 mt-1">Escolha o metodo de impressao para recibos termicos</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="method-selector">
          <Button variant={method === "qz" ? "default" : "outline"} onClick={() => setMethod("qz")}
            className={`gap-1.5 text-xs h-10 ${method === "qz" ? "bg-orange-600 hover:bg-orange-700" : "border-orange-300"}`} data-testid="method-qz-btn">
            <Wifi className="w-4 h-4" /> QZ Tray
          </Button>
          <Button variant={method === "desktop" ? "default" : "outline"} onClick={() => { setMethod("desktop"); saveMethod("desktop"); }}
            className={`gap-1.5 text-xs h-10 ${method === "desktop" ? "bg-amber-600 hover:bg-amber-700" : ""}`} data-testid="method-desktop-btn">
            <Download className="w-4 h-4" /> Print Station
          </Button>
          <Button variant={method === "serial" ? "default" : "outline"} onClick={() => setMethod("serial")}
            className={`gap-1.5 text-xs h-10 ${method === "serial" ? "bg-emerald-600 hover:bg-emerald-700" : ""}`} data-testid="method-serial-btn">
            <Cable className="w-4 h-4" /> Porta Serial
          </Button>
          <Button variant={method === "browser" ? "default" : "outline"} onClick={() => setMethod("browser")}
            className={`gap-1.5 text-xs h-10 ${method === "browser" ? "bg-violet-600 hover:bg-violet-700" : ""}`} data-testid="method-browser-btn">
            <Monitor className="w-4 h-4" /> Navegador
          </Button>
          <Button variant="outline" onClick={() => window.open("/printer-guide", "_blank")}
            className="gap-1.5 text-xs h-10 border-blue-300 text-blue-600 hover:bg-blue-50" data-testid="open-guide-link">
            <BookOpen className="w-4 h-4" /> Guia
          </Button>
        </div>

        {method === "desktop" && <DesktopMethodSection />}

        {method === "serial" && <SerialPrinterSection {...serial} />}

        {method === "browser" && (
          <BrowserMethodSection
            browserTestPrinting={setup.browserTestPrinting}
            handleTestBrowser={setup.handleTestBrowser}
            handleDownloadReceipt={setup.handleDownloadReceipt}
          />
        )}

        {method === "qz" && (
          <QZTraySection
            qzStatus={setup.qzStatus}
            qzError={setup.qzError}
            printers={setup.printers}
            testPrinting={setup.testPrinting}
            selectedPrinter={setup.selectedPrinter}
            setSelectedPrinter={setup.setSelectedPrinter}
            tryConnect={setup.tryConnect}
            testPrint={setup.testPrint}
            installerDownloading={setup.installerDownloading}
            downloadInstaller={setup.downloadInstaller}
            printStations={setup.printStations}
            savingStations={setup.savingStations}
            addStation={setup.addStation}
            removeStation={setup.removeStation}
            updateStation={setup.updateStation}
            saveStations={setup.saveStations}
          />
        )}

        <PrintDiagnosticCard
          diagnostic={setup.diagnostic}
          diagRunning={setup.diagRunning}
          runDiagnostic={setup.runDiagnostic}
        />

        <div className="border-t pt-3">
          <p className="text-xs text-gray-400 flex items-center gap-1">
            <Monitor className="w-3 h-3" />
            Metodo ativo: {method === "desktop" ? "Print Station" : method === "qz" ? "QZ Tray" : method === "serial" ? "Porta Serial" : "Navegador"}
            {setup.selectedPrinter && method === "qz" && ` → ${setup.selectedPrinter}`}
          </p>
        </div>
      </div>
    </MainLayout>
  );
}
